import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../features/auth/hooks/useCurrentUser';
import { WorkspacesApiService } from '../services/WorkspacesApiService';
import type { WorkspaceFull, ConflictPayload, WorkspaceUpdateSuccess } from '../services/WorkspacesApiService';
import { useDomainContext } from '../contexts/DomainContext';

export const QUERY_KEY = 'workspaces';

// ---------------------------------------------------------------------------
// HOOK: useGetWorkspaces
// Lists all workspace metadata for the current user.
// ---------------------------------------------------------------------------
export function useGetWorkspaces() {
    const user = useCurrentUser();
    const { activeDomainId } = useDomainContext();
    const ownerId: string = user?.id ?? '';
    const ownerEmail: string = user?.email ?? '';

    return useQuery<WorkspaceFull[]>({
        queryKey: [QUERY_KEY, ownerId, activeDomainId],
        queryFn: () => WorkspacesApiService.list(ownerId, ownerEmail, activeDomainId ?? undefined),
        enabled: !!ownerId && !!activeDomainId,
        staleTime: Infinity,
        gcTime: 1000 * 60 * 10, // 10 minutes
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
}

// ---------------------------------------------------------------------------
// HOOK: useGetWorkspace
// Fetches the full workspace (including state_json) for a given id.
// ---------------------------------------------------------------------------
export function useGetWorkspace(workspaceId: string | null) {
    const user = useCurrentUser();
    const { activeDomainId } = useDomainContext();
    const ownerId: string = user?.id ?? '';
    const ownerEmail: string = user?.email ?? '';

    return useQuery<WorkspaceFull>({
        queryKey: [QUERY_KEY, ownerId, activeDomainId, workspaceId],
        queryFn: () => WorkspacesApiService.get(ownerId, ownerEmail, workspaceId!, activeDomainId ?? undefined),
        enabled: !!ownerId && !!workspaceId && !!activeDomainId,
        staleTime: Infinity,
        gcTime: 1000 * 60 * 10, // 10 minutes
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
}

// ---------------------------------------------------------------------------
// HOOK: useCreateWorkspace
// ---------------------------------------------------------------------------
export function useCreateWorkspace() {
    const queryClient = useQueryClient();
    const user = useCurrentUser();
    const { activeDomainId } = useDomainContext();
    const ownerId: string = user?.id ?? '';
    const ownerEmail: string = user?.email ?? '';

    return useMutation({
        mutationFn: ({
            id,
            name,
            stateJson,
        }: {
            id: string;
            name: string;
            stateJson: Record<string, any>;
        }) => WorkspacesApiService.create(ownerId, ownerEmail, id, name, stateJson, activeDomainId ?? undefined),
        onSuccess: (_result, variables) => {
            const nowIso = new Date().toISOString();
            const createdMeta: WorkspaceFull = {
                id: variables.id,
                name: variables.name,
                is_monitor_tab: false,
                version: 1,
                created_at: nowIso,
                updated_at: nowIso,
                owner_id: ownerId,
                owner_email: ownerEmail,
                state_json: variables.stateJson,
            };

            queryClient.setQueryData<WorkspaceFull[]>([QUERY_KEY, ownerId, activeDomainId], (prev) => {
                if (!prev || prev.length === 0) return [createdMeta];
                const withoutCreated = prev.filter((w) => w.id !== variables.id);
                return [...withoutCreated, createdMeta];
            });

            queryClient.setQueryData<WorkspaceFull>(
                [QUERY_KEY, ownerId, activeDomainId, variables.id],
                createdMeta
            );
        },
    });
}

// ---------------------------------------------------------------------------
// HOOK: useUpdateWorkspace
// Sends a PUT with an If-Match header. Returns conflict payload instead of
// throwing so the caller can show a merge/reload dialog.
// ---------------------------------------------------------------------------
export function useUpdateWorkspace() {
    const queryClient = useQueryClient();
    const user = useCurrentUser();
    const { activeDomainId } = useDomainContext();
    const ownerId: string = user?.id ?? '';
    const ownerEmail: string = user?.email ?? '';

    return useMutation<
        WorkspaceUpdateSuccess | ConflictPayload,
        Error,
        { id: string; name?: string; stateJson?: Record<string, any>; version: number }
    >({
        mutationFn: ({ id, name, stateJson, version }) =>
            WorkspacesApiService.update(
                ownerId,
                ownerEmail,
                id,
                { name, state_json: stateJson },
                version,
                activeDomainId ?? undefined
            ),
        onSuccess: (result, variables) => {
            if ('success' in result && result.success) {
                // 1. Update specific workspace cache
                queryClient.setQueryData<WorkspaceFull>(
                    [QUERY_KEY, ownerId, activeDomainId, variables.id],
                    (old) => {
                        if (!old) return old;
                        const nextVersion = result.version ?? variables.version;
                        return {
                            ...old,
                            name: variables.name ?? old.name,
                            state_json: variables.stateJson ?? old.state_json,
                            version: nextVersion,
                        };
                    }
                );

                // 2. Update workspace list cache to keep version numbers in sync
                // This prevents the "backendWorkspaces" in Universe.tsx from having stale versions
                queryClient.setQueryData<WorkspaceFull[]>(
                    [QUERY_KEY, ownerId, activeDomainId],
                    (oldList) => {
                        if (!oldList) return oldList;
                        const nextVersion = result.version ?? variables.version;
                        return oldList.map((ws) => {
                            if (ws.id === variables.id) {
                                return {
                                    ...ws,
                                    name: variables.name ?? ws.name,
                                    version: nextVersion,
                                    // Note: we don't necessarily need state_json in the list cache 
                                    // if it's only used for metadata, but it doesn't hurt.
                                    state_json: variables.stateJson ?? ws.state_json,
                                };
                            }
                            return ws;
                        });
                    }
                );
            }
            // Logic change: We NO LONGER invalidate queries here. 
            // This ensures that the page state is only loaded once at the start,
            // as requested by the user. This prevents the redundant GET after PUT.
        },
    });
}

// ---------------------------------------------------------------------------
// HOOK: useForceUpdateWorkspace
// Bypasses version check — only called explicitly after the user confirms overwrite.
// ---------------------------------------------------------------------------
export function useForceUpdateWorkspace() {
    const user = useCurrentUser();
    const { activeDomainId } = useDomainContext();
    const ownerId: string = user?.id ?? '';
    const ownerEmail: string = user?.email ?? '';

    return useMutation<WorkspaceUpdateSuccess, Error, { id: string; name?: string; stateJson?: Record<string, any> }>({
        mutationFn: ({ id, name, stateJson }) =>
            WorkspacesApiService.forceUpdate(ownerId, ownerEmail, id, { name, state_json: stateJson }, activeDomainId ?? undefined),
    });
}

// ---------------------------------------------------------------------------
// HOOK: useDeleteWorkspace
// ---------------------------------------------------------------------------
export function useDeleteWorkspace() {
    const queryClient = useQueryClient();
    const user = useCurrentUser();
    const { activeDomainId } = useDomainContext();
    const ownerId: string = user?.id ?? '';
    const ownerEmail: string = user?.email ?? '';

    return useMutation<void, Error, { id: string }>({
        mutationFn: ({ id }) => WorkspacesApiService.remove(ownerId, ownerEmail, id, activeDomainId ?? undefined),
        onSuccess: (_result, variables) => {
            queryClient.setQueryData<WorkspaceFull[]>(
                [QUERY_KEY, ownerId, activeDomainId],
                (prev) => (prev ?? []).filter((w) => w.id !== variables.id)
            );

            queryClient.removeQueries({
                queryKey: [QUERY_KEY, ownerId, activeDomainId, variables.id],
                exact: true,
            });
        },
    });
}
